import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Redirect, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import styles from '../../views/ProductDetails/ProductDetails.module.scss';
import miniPDPStyles from './MiniPDP.module.scss';
import HideChatWidget from '../../components/ZendeskChat/HideChatWidget';
import AppPaths from '../../AppPaths';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useFeatureValue } from '@growthbook/growthbook-react';
import CollarAddToBagButton from '../../views/ProductDetails/Series3CollarDetails/components/CollarAddToBagButton';
import { COLOR_PACK_IMAGE_URL, DesktopImageGallery, useMiniGalleryMedia } from './MiniImageGallery/MiniImageGallery';
import * as types from '../../types';
import { Species } from '../../types/gql-op-types';
import Footer from '../Footer/Footer';
import CrossShopSection from '../CrossShopSection/CrossShopSection';
import { Carousel, CarouselController } from '../Carousel';
import { useSpeciesUrlParam } from '../../hooks/useSpeciesUrlParam';
import SubscriptionOptions from '../SubscriptionOptions';
import { filterSixMonthPlanForExperiment } from '../SubscriptionOptions/hideSixMonthPlanTest';
import MiniAllFeatures from './MiniAllFeatures/MiniAllFeatures';
import MiniProductSummary from './MiniProductSummary/MiniProductSummary';
import PlusMembershipValueProps from '../../views/ProductDetails/Series3CollarDetails/components/PlusMembershipValueProps';
import { cartActions } from '../../reducers/storeCart';
import * as events from '../../lib/analytics/events';
import {
  getAnalyticsProductPriceInCents,
  getAnalyticsSubscriptionValueInCents,
} from '../../lib/analytics/analyticsUtils';
import { Image as FiImage } from '../../components/Image';
import MiniFAQ from './MiniFAQ/MiniFAQ';
import PlusNewFeatures from '../../views/ProductDetails/Series3CollarDetails/components/PlusNewFeatures';
import useSubscriptionProducts from '../../hooks/useSubscriptionProducts';
import { collarProductIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';
import { formatPromoDiscountDisplay } from '../SubscriptionOptionDetails/SubscriptionOptionDetails';
import NavBar from '../AppBar/NavBar';
import SubscriptionUpsellModal from '../SubscriptionUpsellModal';
import {
  getNanoVariantPetGenderSelect,
  getNanoVariantPetName,
  hasNanoVariantPetName,
  showNanoVariant,
} from '../NanoBanner/NanoBanner';
import RescueOfferBox from './Rescue/RescueOfferBox';
import RescueSections from './Rescue/RescueSections';
import NanoProductDetails from '../../views/ProductDetails/Series3CollarDetails/components/NanoProductDetails/NanoProductDetails';
import { showRescueGwaVariant, showRescuePdpRedesign } from '../../views/RescueLanding/RescueBanner';
import { CustomerIO } from '../../lib/analytics/customerIO';
import { isInAppShop } from '../../lib/inAppShop';
import { Box, Flex, Text } from '@mantine/core';
import { useIsGift } from '../../lib/util/isGift';
import { ReactComponent as IconInfo } from '../../assets/images/icons/icon_info_transparent.svg';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { getFreeTrialDurationLabel } from '../../lib/subscription';
import { usePdpImagesContext } from '../../contexts/PdpImagesContext';
import useFreeTrialSubscriptionPrice from '../../hooks/useFreeTrialSubscriptionPrice';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import StarRating from '../StarRating/StarRating';
import OutcomesChecklist from '../OutcomesChecklist/OutcomesChecklist';
import useFreeTrialPromoVisible from '../../hooks/useFreeTrialPromoVisible';
import useHardwareFirstCheckout from '../../hooks/useHardwareFirstCheckout';
import useHardwareFirstAddGuard from '../../hooks/useHardwareFirstAddGuard';
import useHardwareFirstPdpPricing from '../../hooks/useHardwareFirstPdpPricing';
import HardwareFirstDisclosure from '../HardwareFirstDisclosure/HardwareFirstDisclosure';
import { centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import useFreeTrialEligibility from '../../hooks/useFreeTrialEligibility';
import { captureQuizReferralFromUrl, getQuizReferral, QUIZ_PRODUCT_MINI } from '../../lib/quizReferral';

function MiniAppCarousel({ species }: { species: Species }) {
  const ecomMiniBehavior = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-behavior', false);

  const catImages = ['/product_images/smart-collar-v3b/app-ui-homescreen-1-cat.png'];

  const dogImages = [
    '/product_images/smart-collar-v3b/app-ui-homescreen-1-dog.png',
    ...(ecomMiniBehavior
      ? [
          '/product_images/smart-collar-v3b/app-ui-homescreen-2-dog.png',
          '/product_images/smart-collar-v3b/app-ui-homescreen-3-dog.png',
        ]
      : []),
  ];

  const images = species === Species.CAT ? catImages : dogImages;

  return (
    <Carousel
      className={miniPDPStyles.mobileAppCarousel}
      wrapAround={true}
      prevNextButtons={species === Species.DOG && ecomMiniBehavior}
    >
      {images.map((url, index) => (
        <div key={index} className={miniPDPStyles.mobileAppImage}>
          <FiImage
            image={{
              type: 'image',
              url: url,
              descriptors: ['1000w'],
            }}
            loading="eager"
          />
        </div>
      ))}
    </Carousel>
  );
}

const useUpsellSubscription = (
  selectedSubscription: types.ISubscriptionProduct | undefined,
  subscriptionProducts: types.ISubscriptionProduct[],
) => {
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const upsellEnabled = getFiGrowthBook().getFeatureValue<boolean>('enable-3mo-mini-upsell', false);

  const subscriptionToUpsell: types.ISubscriptionProduct | undefined = useMemo(() => {
    if (!selectedSubscription) {
      return undefined;
    }

    const planCodeToUpsell = selectedSubscription.attemptUpsellToPlanCode;
    if (!planCodeToUpsell) {
      return undefined;
    }

    return subscriptionProducts.find((p) => p.sku === planCodeToUpsell);
  }, [selectedSubscription, subscriptionProducts]);

  return { showUpsellModal, setShowUpsellModal, subscriptionToUpsell, upsellEnabled };
};

export interface MiniPDPProps {
  availableSubscriptions?: types.ISubscriptionProduct[];
  hasValidRedemptionCode?: boolean;
  redemptionType?: types.RedemptionType | null;
  redemptionDiscountInCents?: number | null;
  preselectedSubscriptionSku?: string;
}

function ProductTitle({
  species,
  freeTrialEnabled,
  rescueVariant,
}: {
  species: Species;
  freeTrialEnabled: boolean;
  rescueVariant?: boolean;
}) {
  const isGift = useIsGift();
  const { t } = useTranslation();
  const saleBadge = useFeatureValue('ecom-bfcm-badge', 0);

  if (isGift) {
    return (
      <Flex align={'baseline'} gap={8}>
        <Text size={48} fw={700} lh={1.05} sx={{ letterSpacing: '-0.96px' }}>
          <Trans i18nKey="give_the_gift_of_fi_mini" components={{ br: <br /> }} />
        </Text>
      </Flex>
    );
  }

  // the rescue variant leads with the full product name and drops the NEW chip: an adopter has no
  // baseline to read "new" against, and the name is doing the introducing
  return (
    <div className={classNames(miniPDPStyles.productTitle, { [miniPDPStyles.rescueProductTitle]: rescueVariant })}>
      {rescueVariant ? t('fi_mini') : t('mini')}{' '}
      <span className={miniPDPStyles.productTitleSubtitle}>{species === Species.CAT ? t('for_cats') : ''}</span>
      <SaleBadge className={miniPDPStyles.saleBadge} freeTrialEnabled={freeTrialEnabled} />
      {!saleBadge && !freeTrialEnabled && !rescueVariant && <span className={miniPDPStyles.newLabel}>{t('new')}</span>}
    </div>
  );
}

function ProductTitleDescription({
  species,
  showNanoDescription,
  showRescueDescription,
  quizPetName,
}: {
  species: Species;
  showNanoDescription: boolean;
  showRescueDescription: boolean;
  quizPetName?: string;
}) {
  const isGift = useIsGift();
  const { t } = useTranslation();
  if (isGift) {
    return <div className={miniPDPStyles.productTitleDescription}>{t('gift_a_fi_mini')}</div>;
  }

  // personalized only when the adopter link carries a usable ?pet_name= (the rescue portal will
  // append them; nothing does yet in prod). pronouns come from an ICU select over ?pet_gender=
  // with a neutral branch, so an absent or unrecognized gender never guesses - fi pets split
  // roughly evenly, so a hardcoded pronoun would be wrong for about half of adopters.
  if (showRescueDescription) {
    const rescueDescriptionClass = classNames(
      miniPDPStyles.productTitleDescription,
      miniPDPStyles.rescueProductTitleDescription,
    );
    if (hasNanoVariantPetName()) {
      return (
        <div className={rescueDescriptionClass}>
          {t('rescue_mini_subtitle_named', {
            petName: getNanoVariantPetName(species),
            gender: getNanoVariantPetGenderSelect(),
          })}
        </div>
      );
    }
    return <div className={rescueDescriptionClass}>{t('rescue_mini_subtitle')}</div>;
  }

  if (showNanoDescription) {
    const petName = getNanoVariantPetName(species);
    return (
      <div className={miniPDPStyles.productTitleDescription}>
        {t('microchips_are_great_start_but_they_cant_tell_you_where_pet_is', { petName })}
      </div>
    );
  }

  if (quizPetName) {
    return (
      <div className={miniPDPStyles.productTitleDescription}>
        {t('quiz_mini_subtitle_for_pet', { petName: quizPetName })}
      </div>
    );
  }

  return (
    <div className={miniPDPStyles.productTitleDescription}>
      {species === Species.DOG
        ? t('track_your_dog_with_our_smallest_most_flexible_gps_device_yet')
        : t('slim_light_and_made_to_fit_any_setup_our_gps_tracker_built_for_curious_cats')}
    </div>
  );
}

function ProductGiftDisclaimer() {
  const { t } = useTranslation();
  return (
    <Flex align="center" gap={8} mb={24} sx={(theme) => ({ color: theme.colors['type-light-secondary'] })}>
      <Box h={40} w={40} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <IconInfo />
      </Box>
      <Text size={12} fw={500} lh={1.4}>
        {t('gift_disclaimer')}
      </Text>
    </Flex>
  );
}

export default function MiniPDP({
  availableSubscriptions,
  hasValidRedemptionCode,
  redemptionType,
  redemptionDiscountInCents,
  preselectedSubscriptionSku,
}: MiniPDPProps) {
  const isGift = useIsGift();
  const isReferralV2 = useIsReferralCheckoutV2();

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const products = useSelector((state: types.AppState) => state.config.products);
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const [species, setSpecies] = useSpeciesUrlParam();
  const carouselControllerRef = useRef<CarouselController>();
  const previousCarouselIndexRef = useRef(0);

  // Get pre-loaded dynamic images from context
  const { getMedia } = usePdpImagesContext();
  const dynamicImages = isGift ? null : getMedia(types.miniCollarId, { species: species.toLowerCase() });
  // Same list the gallery renders - read here so the light-mode slide indices below can be derived
  // from it rather than hardcoded.
  const galleryMedia = useMiniGalleryMedia(species, dynamicImages);
  const [currentCarouselImage, setCurrentCarouselImage] = useState<number>(0);
  const specsRef = useRef<HTMLDivElement | null>(null);
  const featuresRef = useRef<HTMLDivElement | null>(null);
  const miniPlusRef = useRef<HTMLDivElement | null>(null);
  const faqRef = useRef<HTMLDivElement | null>(null);
  const rescueOfferBoxRef = useRef<HTMLDivElement | null>(null);
  const { country, currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const referrerName = useSelector((state: types.AppState) => state.storeShop.cart.referrerName);
  const rescueGwaVariantEnabled = showRescuePdpRedesign();
  const nanoVariantEnabled = showNanoVariant();
  const simplifiedVariantEnabled = rescueGwaVariantEnabled || nanoVariantEnabled;
  const ecomMiniBehavior = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-behavior', false);

  const product = products.find((p) => p.category === types.ProductCategory.MINI) as types.IMiniProduct | undefined;

  // arm applies only to the plain d2c pdp: gift, rescue (both pdp-experiment arms), nano, and
  // gwp-redemption flows keep the control experience (partner promos must not mix with the new
  // pricing model). showRescueGwaVariant rather than the redesign variant: control-arm rescue
  // visitors still carry the rescue coupon and entitlement cookies. redemptionType rather than
  // hasValidRedemptionCode: the redeem pdp sets it even when the code is missing or invalid, and
  // that whole context stays control
  const hardwareFirstEnrolled =
    useHardwareFirstCheckout() && !isGift && !showRescueGwaVariant() && !nanoVariantEnabled && !redemptionType;
  const hardwareFirstDiscount = useHardwareFirstPdpPricing(hardwareFirstEnrolled ? product?.sku : undefined);
  const { atCap: hardwareFirstAtCap, routeToBag: hardwareFirstRouteToBag } = useHardwareFirstAddGuard();
  const allMiniSubscriptions = useSubscriptionProducts(product?.id ?? types.miniCollarId);
  const defaultSubscriptions = allMiniSubscriptions.filter(
    (p) =>
      (siteConfig.miniPlusByDefault ? p.isPlusPlan : !p.isPlusPlan) &&
      // filter out the Verizon plan (or any third-party plans) on D2C PDPs
      !p.thirdParty,
  );
  const sixMonthSubscriptions = defaultSubscriptions.filter((s) => s.renewalMonths === 6);
  const giftSubscriptions = defaultSubscriptions.filter((s) => [6, 12, 24].includes(s.renewalMonths));
  const subscriptions = filterSixMonthPlanForExperiment(
    isGift
      ? giftSubscriptions
      : simplifiedVariantEnabled
        ? sixMonthSubscriptions
        : (availableSubscriptions ?? defaultSubscriptions),
  );
  let initialSubscription = preselectedSubscriptionSku
    ? subscriptions.find((s) => s.sku === preselectedSubscriptionSku)
    : undefined;

  if (initialSubscription === undefined && simplifiedVariantEnabled) {
    initialSubscription = subscriptions.at(0);
  }

  const { isEligible: isFreeTrialEligible } = useFreeTrialEligibility(product?.id ?? types.miniCollarId);
  const referralFreeTrialSubscription =
    isReferralV2 && isFreeTrialEligible ? subscriptions.find((s) => s.freeTrial) : undefined;

  const quizReferral = getQuizReferral();
  const quizReferredMini = !isGift && quizReferral?.recommendedProduct === QUIZ_PRODUCT_MINI;
  const quizPetName = quizReferredMini ? quizReferral?.petName : undefined;
  // Quiz trial CTA continuity: preselect the 12-month free-trial plan the quiz offered.
  const freeTrialPlans = subscriptions.filter((s) => s.freeTrial);
  const quizTrialFreeTrialSubscription =
    quizReferredMini && quizReferral?.ctaType === 'trial' && isFreeTrialEligible
      ? (freeTrialPlans.find((s) => s.renewalMonths === 12) ?? freeTrialPlans[0])
      : undefined;

  // The default reflects async inputs (plan config, trial eligibility): the
  // referral/quiz trial plans resolve to undefined until eligible, so this
  // naturally lands on the trial once it's available and reverts to the
  // regular plan if the shopper becomes ineligible (trial already in cart,
  // existing member) — no effects to sync.
  const defaultSubscription = referralFreeTrialSubscription ?? quizTrialFreeTrialSubscription ?? initialSubscription;

  // Track only the shopper's explicit pick; fall back to the default. Ignore a
  // stale pick that's no longer in the list (e.g. after a species switch), or
  // a trial pick after eligibility flipped (the chooser hides the trial, so
  // the hidden plan must not stay submittable).
  const [pickedSubscriptionSku, setPickedSubscriptionSku] = useState<string>();
  const selectedSubscription = useMemo(() => {
    const picked =
      pickedSubscriptionSku &&
      subscriptions.find((s) => s.sku === pickedSubscriptionSku && (!s.freeTrial || isFreeTrialEligible));
    return picked || defaultSubscription;
  }, [pickedSubscriptionSku, subscriptions, defaultSubscription, isFreeTrialEligible]);

  // sourced from the selected plan rather than written into the copy: not every market charges one,
  // and the offer box drops the sentence entirely rather than promise a fee that never appears.
  const rescueHasActivationFee = !!selectedSubscription?.activationFeeInCents;

  // mirrors the country gate the plan card applies to its own promo badge, so the offer box never
  // claims the offer in a market where the card would not. resolving the amount through the same
  // helper the card uses is what proves the promo is live here, even though the badge no longer
  // prints the figure.
  const rescueOfferBadgeAvailable =
    rescueGwaVariantEnabled &&
    getFiGrowthBook().getFeatureValue<boolean>('ecom-subscription-options-offer-tag-mini', false) &&
    getFiGrowthBook()
      .getFeatureValue<string[]>('ecom-promo-banner-supported-countries', ['US', 'CA', 'GB'])
      .includes(country)
      ? formatPromoDiscountDisplay(undefined, selectedSubscription?.renewalMonths ?? 6, wholeNumberCurrencyFormatter)
      : null;

  // the renewal line under the cta quotes the 12-month plan the membership renews onto. sourced
  // from the loaded products rather than a literal so currency and price moves track config; when
  // no 12-month plan is in the payload the line simply does not render.
  const rescueRenewalPrice = useMemo(() => {
    const yearlyPlan = subscriptions?.find((s) => s.renewalMonths === 12 && !s.freeTrial);
    return yearlyPlan
      ? wholeNumberCurrencyFormatter.format(
          centsToDollarsNumber(getLocalizedPricing(yearlyPlan.pricingInCents, currency)),
        )
      : null;
  }, [subscriptions, wholeNumberCurrencyFormatter, currency]);

  const subscriptionPickerRef = useRef<HTMLDivElement>(null);

  const { showUpsellModal, setShowUpsellModal, subscriptionToUpsell, upsellEnabled } = useUpsellSubscription(
    selectedSubscription,
    subscriptions,
  );

  const freeTrialPromoVisible = useFreeTrialPromoVisible();
  const freeTrialEnabled = freeTrialPromoVisible && subscriptions.some((p) => p.freeTrial);
  const freeTrialSubscriptionPrice = useFreeTrialSubscriptionPrice(product?.id ?? types.miniCollarId);

  useEffect(() => {
    captureQuizReferralFromUrl();
  }, []);

  // Analytics - Mini product page viewed
  useEffect(() => {
    if (!product) return;
    const totalPriceInCents =
      getAnalyticsProductPriceInCents(product, currency) +
      (selectedSubscription ? getAnalyticsProductPriceInCents(selectedSubscription, currency) : 0);

    events.miniProductViewed({
      priceInCents: totalPriceInCents,
      productName: product.name,
      sku: product.sku,
      speciesName: species === Species.CAT ? 'Cat' : 'Dog',
      isGift,
      currency,
      analyticsSubscriptionValueInCents: selectedSubscription
        ? getAnalyticsSubscriptionValueInCents(selectedSubscription, currency)
        : undefined,
    });
  }, [product, selectedSubscription, isGift, currency, species]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const observeOnce = (element: Element | null, onIntersect: () => void) => {
      if (!element) return;
      const observer = new IntersectionObserver(
        (entries, obs) => {
          const isVisible = entries.some((e) => e.isIntersecting);
          if (isVisible) {
            onIntersect();
            obs.disconnect();
          }
        },
        { threshold: 0.25 },
      );
      observer.observe(element);
      observers.push(observer);
    };

    observeOnce(specsRef.current, () => events.miniViewedSpecifications());
    observeOnce(featuresRef.current, () => events.miniViewedFeatures());
    observeOnce(miniPlusRef.current, () => events.miniViewedMiniPlus());
    observeOnce(faqRef.current, () => events.miniViewedFAQ());
    observeOnce(rescueOfferBoxRef.current, () => events.miniOfferShown());

    return () => {
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  const addToBag = useCallback(
    (subscription: types.ISubscriptionProduct | undefined) => {
      if (hardwareFirstEnrolled && product) {
        // at the per-cart unit cap: go to the bag, where the bulk-sales notice explains
        if (hardwareFirstAtCap) {
          hardwareFirstRouteToBag();
          return true;
        }
        events.miniAddedToBag({
          priceInCents:
            hardwareFirstDiscount?.discountedPriceInCents ?? getAnalyticsProductPriceInCents(product, currency),
          productName: product.name,
          sku: product.sku,
          isGift,
          currency,
        });
        dispatch(cartActions.addSingleLineCartItem({ sku: product.sku, currency }));
        CustomerIO.trackAddedItemToCart(types.miniCollarId);
        hardwareFirstRouteToBag();
        return true;
      }

      if (!subscription || !product) {
        subscriptionPickerRef.current?.scrollIntoView({ behavior: 'smooth' });
        events.miniPickYourMembershipClicked();
        return false;
      }

      // Fire add to bag analytics event
      const totalPriceInCents =
        getAnalyticsProductPriceInCents(product, currency) + getAnalyticsProductPriceInCents(subscription, currency);

      events.miniAddedToBag({
        priceInCents: totalPriceInCents,
        productName: product.name,
        sku: product.sku,
        isGift,
        currency,
      });

      dispatch(
        cartActions.addBundleCartItem({
          sku: product.sku,
          subscriptionSku: subscription.sku,
          addonSkus: [],
          currency,
          isGift: isGift,
          species,
        }),
      );

      CustomerIO.trackAddedItemToCart(types.miniCollarId);
      if (isInAppShop()) {
        dispatch(cartActions.setShowInAppCheckout(true));
      } else {
        history.push(AppPaths.Bag);
      }
      return true;
    },
    [
      dispatch,
      product,
      history,
      subscriptionPickerRef,
      currency,
      isGift,
      species,
      hardwareFirstEnrolled,
      hardwareFirstDiscount,
      hardwareFirstAtCap,
      hardwareFirstRouteToBag,
    ],
  );

  const onAddToBagClicked = useCallback(() => {
    if (subscriptionToUpsell && upsellEnabled && !hardwareFirstEnrolled) {
      setShowUpsellModal(true);
      return false;
    }

    return addToBag(selectedSubscription);
  }, [setShowUpsellModal, subscriptionToUpsell, addToBag, selectedSubscription, upsellEnabled, hardwareFirstEnrolled]);

  // Handler for when user clicks "Start free trial" in the modal - adds to cart
  const handleFreeTrialAddToCart = useCallback(
    (subscriptionSku: string) => {
      const subscription = subscriptions.find((s) => s.sku === subscriptionSku);
      if (subscription) {
        addToBag(subscription);
      }
    },
    [subscriptions, addToBag],
  );

  const config = useSelector((state: types.AppState) => state.config.siteConfig);
  // coerced like every other internalTester call site: an undefined value is dropped from the
  // serialized attributes, giving this call a different shape than the rest of the page's
  const miniPDPEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-pdp', false, {
    internalTester: !!config.internalTester,
  });
  if (!miniPDPEnabled) {
    return <Redirect to={AppPaths.NotFound} />;
  }
  if (!collarProductIsSupportedInCountry(types.miniCollarId, country)) {
    return <Redirect to={AppPaths.Products(types.series3PlusCollarId)} />;
  }

  let lightModeImages: number[] = [];
  if (species === Species.CAT) {
    lightModeImages = [0, 2, 3];
  } else {
    lightModeImages = [0, 2, 3, 4];
  }
  // the rescue hero occupies slide 0 on a light background and shifts the stock gallery by one
  if (rescueGwaVariantEnabled) {
    lightModeImages = [0, ...lightModeImages.map((imageIndex) => imageIndex + 1)];
  }
  // the color pack slide sits on the same near-white background, so the nav needs its dark
  // treatment there too - and inserting it mid-gallery pushes every slide behind it along by one.
  // Both come off the gallery's own media list rather than another hardcoded offset, since where
  // the slide lands moves with species, path and the rescue hero.
  const colorPackIndex = galleryMedia.findIndex((item) => item.url === COLOR_PACK_IMAGE_URL);
  if (colorPackIndex !== -1) {
    lightModeImages = [
      colorPackIndex,
      ...lightModeImages.map((imageIndex) => (imageIndex >= colorPackIndex ? imageIndex + 1 : imageIndex)),
    ];
  }
  const isLightMode = lightModeImages.includes(currentCarouselImage);

  return (
    <>
      {selectedSubscription && subscriptionToUpsell && upsellEnabled && (
        <SubscriptionUpsellModal
          onAccept={() => addToBag(subscriptionToUpsell)}
          onClose={() => setShowUpsellModal(false)}
          onIgnore={() => addToBag(selectedSubscription)}
          open={showUpsellModal}
          selectedSubscription={selectedSubscription}
          subscriptionToUpsell={subscriptionToUpsell}
        />
      )}
      <div
        className={classNames(styles.productContainer, styles.ecomFlowRevamp, 'ecom-flow-revamp', 'mini-pdp', {
          'ecom-flow-revamp-light-v2': isLightMode,
          // unhashed on purpose. the desktop rescue layout overrides `.ecom-flow-revamp` rules that
          // live in two other stylesheets, where a hashed class from this component is unreachable -
          // each override has to sit in the same file as the rule it beats for source order to settle
          // the specificity tie. this marker is what those files scope on.
          'rescue-pdp': rescueGwaVariantEnabled,
        })}
      >
        <NavBar />
        <div
          className={classNames('image-gallery-container', styles.galleryContainer, {
            // miniPDPStyles, not styles: `styles` is ProductDetails.module.scss
            [miniPDPStyles.rescueGallery]: rescueGwaVariantEnabled,
          })}
        >
          <DesktopImageGallery
            carouselControllerRef={carouselControllerRef}
            species={species}
            onChange={(idx) => {
              if (idx !== previousCarouselIndexRef.current) {
                const direction = idx > previousCarouselIndexRef.current ? 'next' : 'prev';
                events.miniCarouselScrolled(idx, direction);
                previousCarouselIndexRef.current = idx;
              }
              setCurrentCarouselImage(idx);
            }}
            dynamicImages={dynamicImages}
          />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            {isReferralV2 && <StarRating />}
            <ProductTitle
              species={species}
              freeTrialEnabled={freeTrialEnabled}
              rescueVariant={rescueGwaVariantEnabled}
            />
            <ProductTitleDescription
              species={species}
              showNanoDescription={nanoVariantEnabled && !rescueGwaVariantEnabled}
              showRescueDescription={rescueGwaVariantEnabled}
              quizPetName={quizPetName}
            />
            {rescueGwaVariantEnabled && (
              <RescueOfferBox
                hasActivationFee={rescueHasActivationFee}
                showBadge={!!rescueOfferBadgeAvailable}
                containerRef={rescueOfferBoxRef}
              />
            )}
            {isReferralV2 && <OutcomesChecklist />}

            {/* hidden for adopters: the rescue link names the pet's species, and a chooser invites a
                decision the adopter has already made. requires `?type=` on the adopter link to reach
                cat adopters, which the lander now forwards. */}
            <div
              className={classNames(miniPDPStyles.speciesSwitcher, {
                [miniPDPStyles.hidden]: rescueGwaVariantEnabled,
              })}
            >
              <div className={miniPDPStyles.switcherContainer}>
                <div
                  className={classNames(miniPDPStyles.switcherSlider, {
                    [miniPDPStyles.sliderCat]: species === Species.CAT,
                  })}
                />
                <button
                  className={classNames(miniPDPStyles.switcherOption, {
                    [miniPDPStyles.active]: species === Species.DOG,
                  })}
                  onClick={() => {
                    setSpecies(Species.DOG);
                    events.miniSpeciesToggle(Species.DOG);
                    carouselControllerRef.current?.select(0);
                  }}
                  type="button"
                >
                  {t('dog')}
                </button>
                <button
                  className={classNames(miniPDPStyles.switcherOption, {
                    [miniPDPStyles.active]: species === Species.CAT,
                  })}
                  onClick={() => {
                    setSpecies(Species.CAT);
                    events.miniSpeciesToggle(Species.CAT);
                    carouselControllerRef.current?.select(0);
                  }}
                  type="button"
                >
                  {t('cat')}
                </button>
              </div>
            </div>

            {hardwareFirstEnrolled && <HardwareFirstDisclosure discount={hardwareFirstDiscount} />}
            {/* the offer box already states the one plan an adopter gets, so the picker is hidden
                rather than removed: it still runs, and `selectedSubscription` falls back to the
                six-month default, which is what the pinned cta adds to the bag. */}
            {!hardwareFirstEnrolled && (
              <SubscriptionOptions
                allowNoSubscription={false}
                hidden={rescueGwaVariantEnabled}
                onSubscriptionChange={(subscription) => setPickedSubscriptionSku(subscription)}
                selectedSubscriptionSku={selectedSubscription?.sku}
                subscriptionProducts={subscriptions}
                title={isGift ? 'Pick a gift value' : simplifiedVariantEnabled ? '' : t('membership')}
                subtitle={simplifiedVariantEnabled ? undefined : t('every_new_membership_includes_a_free_fi_mini')}
                showReferralPill={false}
                couponPillDisplay={undefined}
                productName={''}
                productCategory={types.ProductCategory.MINI}
                subscriptionPickerRef={subscriptionPickerRef}
                startAsUnselected={!defaultSubscription}
                disableSection={false}
                prominentPricingDesign={false}
                hasValidRedemptionCode={!!hasValidRedemptionCode}
                redemptionType={redemptionType}
                redemptionDiscountInCents={redemptionDiscountInCents}
                onFreeTrialAddToCart={handleFreeTrialAddToCart}
                collarProductId={types.miniCollarId}
              />
            )}
            {isGift && <ProductGiftDisclaimer />}
            <PlusMembershipValueProps freeTrialEnabled={freeTrialEnabled} />
            <CollarAddToBagButton
              ctaText={
                hardwareFirstEnrolled
                  ? t('add_to_cart')
                  : !selectedSubscription
                    ? t('pick_your_membership')
                    : // rescue keeps the plain add-to-cart label over the referral wording: "continue"
                      // added to the bag without saying so, which pulled in lower-intent shoppers
                      isReferralV2 && !rescueGwaVariantEnabled
                      ? referrerName
                        ? t('referral_v2_join_on_fi', { referrerName })
                        : t('referral_v2_join_fi')
                      : t('add_to_cart')
              }
              descriptionText={
                // the rescue bar carries only the button and the renewal line: the term already
                // reads in the offer box, and repeating it here crowded the pinned bar
                rescueGwaVariantEnabled || hardwareFirstEnrolled
                  ? null
                  : selectedSubscription
                    ? selectedSubscription.freeTrial && selectedSubscription.freeTrialDays
                      ? t('free_trial_duration', {
                          duration: getFreeTrialDurationLabel(selectedSubscription.freeTrialDays),
                        })
                      : selectedSubscription.renewalMonths === 12
                        ? t('1_year')
                        : selectedSubscription.renewalMonths === 24
                          ? '2 Years'
                          : selectedSubscription.renewalMonths === 6
                            ? '6 Months'
                            : '1 month'
                    : null
              }
              fadeInRef={selectedSubscription ? undefined : subscriptionPickerRef}
              // the rescue plan is preselected, so the scroll-triggered fade-in would never fire on
              // this path anyway - pinning it outright also covers the window before plans load.
              isVisibleOverride={rescueGwaVariantEnabled || hardwareFirstEnrolled || !!selectedSubscription}
              onAddToBag={() => {
                return onAddToBagClicked();
              }}
              rescueStyling={rescueGwaVariantEnabled}
              subtitleText={
                rescueGwaVariantEnabled
                  ? rescueRenewalPrice
                    ? t('rescue_renews_yearly_after_six_months', { price: rescueRenewalPrice })
                    : null
                  : isReferralV2 && selectedSubscription
                    ? selectedSubscription.freeTrial && freeTrialSubscriptionPrice
                      ? t('referral_v2_renews_yearly_after_trial', { price: freeTrialSubscriptionPrice })
                      : t('referral_v2_renews_at_price_per_period', {
                          price: currencyFormatter.format(
                            centsToDollarsNumber(getLocalizedPricing(selectedSubscription.pricingInCents, currency)),
                          ),
                          period: t(`referral_v2_period_${selectedSubscription.renewalMonths}`),
                        })
                    : selectedSubscription?.freeTrial && freeTrialSubscriptionPrice
                      ? t('membership_after_trial', { price: freeTrialSubscriptionPrice })
                      : null
              }
            />
          </div>
        </div>
      </div>
      {species === Species.DOG && ecomMiniBehavior && !rescueGwaVariantEnabled && (
        <PlusNewFeatures containerRef={miniPlusRef} />
      )}
      {rescueGwaVariantEnabled && <RescueSections />}
      {nanoVariantEnabled && !rescueGwaVariantEnabled && <NanoProductDetails productType="mini" />}
      {/* one call site rather than a guarded pair: the rescue blocks above slot in ahead of the specs
          instead of around them, so both variants want it in the same place */}
      <MiniProductSummary containerRef={specsRef} species={species} />
      <MiniAppCarousel species={species} />
      <MiniAllFeatures containerRef={featuresRef} species={species} />
      <CrossShopSection currentProduct="mini" />
      <MiniFAQ containerRef={faqRef} species={species} />
      <div className={miniPDPStyles.footerContainer}>
        <Footer />
      </div>
      {rescueGwaVariantEnabled && <div className={miniPDPStyles.stickyCtaSpacer} />}
      <HideChatWidget />
    </>
  );
}
